import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { getProducts } from "../lib/api";

export default function Catalogue() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    getProducts().then(setProducts).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(products.map((p) => p.category)))],
    [products]
  );
  const visible = products.filter((p) => filter === "All" || p.category === filter);

  return (
    <div className="grain relative bg-black min-h-screen">
      <Nav />
      <main className="pt-32 pb-24 px-6 md:px-12">
        <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-steel-light mb-3">
          The Full Line
        </p>
        <h1
          data-testid="catalogue-title"
          className="font-display text-5xl md:text-8xl font-extrabold tracking-tighter text-white mb-12 leading-[0.85]"
        >
          Catalogue
        </h1>

        <div className="flex flex-wrap gap-3 mb-14 border-t border-white/10 pt-8">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c}`}
              onClick={() => setFilter(c)}
              className={`font-mono-tech text-[11px] uppercase tracking-widest px-4 py-2 border transition-colors duration-300 ${
                filter === c
                  ? "bg-white text-black border-white"
                  : "text-steel-light border-white/20 hover:border-white hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((p, i) => (
            <motion.div
              key={p.id}
              layout
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.06 }}
            >
              <Link
                to={`/product/${p.id}`}
                data-testid={`catalogue-card-${p.id}`}
                className="group block"
              >
                <div className="relative aspect-[3/4] overflow-hidden border border-white/10 bg-steel-dark/40">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover saturate-[0.5] group-hover:scale-105 transition-transform duration-[900ms] ease-out"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/80 border border-white/20 px-2 py-1">
                      {p.category}
                    </span>
                  </div>
                  <div className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white/0 group-hover:bg-white flex items-center justify-center transition-colors duration-300">
                    <ArrowUpRight className="w-5 h-5 text-white group-hover:text-black transition-colors duration-300" />
                  </div>
                </div>
                <div className="flex items-baseline justify-between mt-4">
                  <h3 className="font-display text-xl font-bold tracking-tight text-white">
                    {p.name}
                  </h3>
                  <span className="font-mono-tech text-sm text-steel-light">
                    ${p.price.toFixed(0)}
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
